import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    LockClosedIcon, 
    CheckCircleIcon, 
    EyeIcon, 
    EyeSlashIcon,
    ShieldCheckIcon 
} from '@heroicons/react/24/outline';
import { changePasswordSra } from '../services/api';

const fontStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@300;400;500&display=swap');
  .font-poppins { font-family: 'Poppins', sans-serif; }
  .font-roboto { font-family: 'Roboto', sans-serif; }
`;

const SetPassword = () => {
    const navigate = useNavigate();
    
    // Retrieve credentials stored during login
    const userEmail = localStorage.getItem('userEmail'); 
    const tempPassword = localStorage.getItem('tempPassword'); 

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', text: '' });

    const handleSetPassword = async (e) => {
        e.preventDefault();
        
        if (!userEmail || !tempPassword) {
            setStatus({ type: 'error', text: 'Session expired. Please log in again.' });
            setTimeout(() => navigate('/login'), 2000);
            return;
        }

        if (password !== confirmPassword) {
            setStatus({ type: 'error', text: 'Passwords do not match!' });
            return;
        }

        if (password.length < 6) {
            setStatus({ type: 'error', text: 'Password must be at least 6 characters.' });
            return;
        }

        setIsLoading(true);
        setStatus({ type: '', text: '' });

        try {
            await changePasswordSra({
                email: userEmail,
                old_password: tempPassword,
                new_password: password
            });

            setStatus({ type: 'success', text: 'Security updated! Redirecting to login...' });
            
            setTimeout(() => {
                localStorage.clear();
                navigate('/login');
            }, 3000);

        } catch (error) {
            const errorMsg = typeof error === 'string' ? error : 'Failed to update password.';
            setStatus({ type: 'error', text: errorMsg });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] p-4 sm:p-6 font-roboto">
            <style>{fontStyles}</style>

            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100"
            >
                {/* Header Section */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-8 text-center">
                    <div className="mx-auto w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 shadow-inner">
                        <ShieldCheckIcon className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white font-poppins tracking-tight">Security Update</h2>
                    <p className="text-indigo-100 text-xs mt-2 font-medium uppercase tracking-widest">Initial Login Setup</p>
                </div>

                <div className="p-8">
                    <p className="text-gray-500 text-sm text-center mb-8">
                        Please choose a permanent password to secure your account.
                    </p>

                    <form onSubmit={handleSetPassword} className="space-y-6">
                        {/* New Password Input */}
                        <div className="relative group">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1.5 ml-1 font-poppins">
                                New Password
                            </label>
                            <div className="relative">
                                <LockClosedIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                                <input 
                                    type={showPassword ? "text" : "password"}
                                    required
                                    className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors"
                                >
                                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password Input */}
                        <div className="relative group">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1.5 ml-1 font-poppins">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <LockClosedIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                                <input 
                                    type={showConfirmPassword ? "text" : "password"}
                                    required
                                    className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-600 transition-colors"
                                >
                                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            </div>
                        </div>

                        {/* Action Button */}
                        <button 
                            disabled={isLoading}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg shadow-indigo-200 flex items-center justify-center disabled:opacity-70 group"
                        >
                            {isLoading ? (
                                <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            ) : (
                                "Update & Sign In"
                            )}
                        </button>
                    </form>

                    {/* Status Messaging */}
                    <AnimatePresence>
                        {status.text && (
                            <motion.div 
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className={`mt-6 p-4 rounded-xl text-xs font-medium flex items-center gap-3 ${
                                    status.type === 'success' 
                                    ? 'bg-green-50 text-green-700 border border-green-100' 
                                    : 'bg-red-50 text-red-700 border border-red-100'
                                }`}
                            >
                                {status.type === 'success' ? (
                                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                                ) : (
                                    <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse flex-shrink-0" />
                                )}
                                {status.text}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="bg-gray-50/50 p-4 text-center border-t border-gray-100">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                        GTPL India Internal Systems
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default SetPassword;